// @services/clients/ContentClient.ts
import type { ContentClient as InteropContentClient } from '@ps-bridge/clients/content';
import type {
    AnswerDocumentQuestionsRequest,
    AnswerSectionQuestionsRequest,
    AnswerSectionQuestionsResponse,
    AppendSectionRequest,
    AppendSectionResponse,
    ContentError,
    DocumentID,
    GenerateDocumentQuestionsResponse,
    GenerateSectionQuestionsRequest,
    GenerateSectionQuestionsResponse,
    RefineRequest,
    RefineResponse,
    ReplaceSectionRequest,
    ReplaceSectionResponse,
    UploadSupplementalDocumentRequest,
} from '@ps-bridge/models/core/dataContracts';
import type { EffectTs } from '@ps-bridge/interop';
import { effectFromRaw } from '@utils/effects/branded';
import type { Ok } from '@ps-bridge/ok';

export interface ContentClient {
    readonly refineSection: (
        documentId: DocumentID,
        request: RefineRequest,
    ) => EffectTs<RefineResponse, ContentError>;
    readonly replaceSection: (
        documentId: DocumentID,
        request: ReplaceSectionRequest,
    ) => EffectTs<ReplaceSectionResponse, ContentError>;
    readonly answerSectionQuestions: (
        documentId: DocumentID,
        request: AnswerSectionQuestionsRequest,
    ) => EffectTs<AnswerSectionQuestionsResponse, ContentError>;
    readonly generateSectionQuestions: (
        documentId: DocumentID,
        request: GenerateSectionQuestionsRequest,
    ) => EffectTs<GenerateSectionQuestionsResponse, ContentError>;
    readonly answerDocumentQuestions: (
        documentId: DocumentID,
        request: AnswerDocumentQuestionsRequest,
    ) => EffectTs<Ok, ContentError>;
    readonly generateDocumentQuestions: (documentId: DocumentID) => EffectTs<GenerateDocumentQuestionsResponse, ContentError>;
    readonly appendSection: (
        documentId: DocumentID,
        request: AppendSectionRequest,
    ) => EffectTs<AppendSectionResponse, ContentError>;
    readonly uploadSupplementalDocument: (
        documentId: DocumentID,
        request: UploadSupplementalDocumentRequest,
    ) => EffectTs<Ok, ContentError>;
}

export const fromInteropContentClient = (client: InteropContentClient): ContentClient => ({
    refineSection: (documentId, request) => effectFromRaw(client.refineSection(documentId)(request)),
    replaceSection: (documentId, request) => effectFromRaw(client.replaceSection(documentId)(request)),
    answerSectionQuestions: (documentId, request) =>
        effectFromRaw(client.answerSectionQuestions(documentId)(request)),
    generateSectionQuestions: (documentId, request) =>
        effectFromRaw(client.generateSectionQuestions(documentId)(request)),
    answerDocumentQuestions: (documentId, request) =>
        effectFromRaw(client.answerDocumentQuestions(documentId)(request)),
    generateDocumentQuestions: (documentId) => effectFromRaw(client.generateDocumentQuestions(documentId)),
    appendSection: (documentId, request) => effectFromRaw(client.appendSection(documentId)(request)),
    uploadSupplementalDocument: (documentId, request) =>
        effectFromRaw(client.uploadSupplementalDocument(documentId)(request)),
});
